import { formatTenantDate } from 'src/app/shared/utils/tenant-date';
import { Permissions } from 'src/app/shared/dto/permissions';
import { PermissionService } from 'src/app/utils/permission.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { LeaveDecision, LeaveRequest } from '../../models/staff-module';
import { StaffModuleApiService, StaffModuleError } from '../../services/staff-module-api.service';
import { MyAquilaApiService } from '../../services/my-aquila-api.service';
import { NotificationService } from 'src/app/utils/notification.service';

/** Colour per leave policy name (stable hash into a small palette). */
const PALETTE = ['#3b82f6', '#ef4444', '#a855f7', '#f59e0b', '#10b981', '#06b6d4', '#ec4899', '#64748b'];

@Component({
  selector: 'app-review-leave-modal',
  templateUrl: './review-leave-modal.component.html',
  styleUrls: ['./review-leave-modal.component.css']
})
export class ReviewLeaveModalComponent implements OnInit, OnDestroy {
  reviewForm: FormGroup;
  request: LeaveRequest;
  /** 'review' lets an approver approve/reject; 'view' is read-only (own requests, with cancel while pending). */
  mode: 'review' | 'view';
  currentStaffId: number | null;
  submitting: LeaveDecision | null = null;
  errorMessage: string | null = null;
  /** 'me' = My Aquila: details come from /my-aquila/leave/requests/{id}; never approve/reject. */
  source: 'staff' | 'me';
  loadingDetail = false;
  detailError: string | null = null;
  private detailSub?: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ReviewLeaveModalComponent>,
    private api: StaffModuleApiService,
    private notification: NotificationService,
    private myApi: MyAquilaApiService,
    private permissionService: PermissionService,
    @Inject(MAT_DIALOG_DATA) public data: { request: LeaveRequest; mode?: 'review' | 'view'; currentStaffId?: number | null; source?: 'staff' | 'me' }
  ) {
    this.request = data.request;
    this.source = data.source === 'me' ? 'me' : 'staff';
    // My Aquila is read-only unless the caller opens a request they review (decided via /my-aquila/leave).
    this.mode = this.source === 'me' ? (data.mode || 'view') : (data.mode || 'review');
    this.currentStaffId = data.currentStaffId ?? null;
  }

  ngOnInit(): void {
    this.reviewForm = this.formBuilder.group({
      reviewComment: ['', []]
    });
    // A request the caller reviews isn't theirs, so the own-request detail call would 404: the row is already complete.
    if (this.isMe && !this.request.toReview) this.loadDetail();
  }

  ngOnDestroy(): void {
    this.detailSub?.unsubscribe();
  }

  get isMe(): boolean {
    return this.source === 'me';
  }

  /** My Aquila: refresh the row with the full request (404 unless it is the caller's own). */
  loadDetail(): void {
    if (!this.request?.id) return;
    this.detailSub?.unsubscribe();
    this.loadingDetail = true;
    this.detailError = null;
    this.detailSub = this.myApi
      .leaveRequest(this.request.id)
      .pipe(finalize(() => (this.loadingDetail = false)))
      .subscribe({
        next: (request) => (this.request = request ?? this.request),
        error: (err: StaffModuleError) =>
          (this.detailError = err?.message || $localize`:@@leaveLoadFailed:Something went wrong. Please try again.`),
      });
  }

  get title(): string {
    return this.mode === 'review'
      ? $localize`:@@reviewLeaveRequest:Review Leave Request`
      : $localize`:@@leaveRequestDetails:Leave Request Details`;
  }

  get isPending(): boolean {
    return this.request.status === 'PENDING';
  }

  get isSelfRequest(): boolean {
    return this.currentStaffId !== null && this.request.staff?.id === this.currentStaffId;
  }

  get canDecide(): boolean {
    // My Aquila rows say whether the caller is the chosen reviewer; the HR view just can't decide its own request.
    if (this.isMe) return this.mode === 'review' && this.isPending && this.request.toReview === true;
    return this.mode === 'review' && this.isPending && !this.isSelfRequest
      && (this.canApprove || this.canReject);
  }

  /** The HR view is role-gated by the backend; My Aquila is limited to the chosen reviewer instead. */
  get canApprove(): boolean {
    return this.isMe || this.permissionService.hasPermissionList([Permissions.APPROVE_LEAVE_REQUEST]);
  }

  get canReject(): boolean {
    return this.isMe || this.permissionService.hasPermissionList([Permissions.REJECT_LEAVE_REQUEST]);
  }

  private get mayCancelStaffRequest(): boolean {
    return this.permissionService.hasPermissionList([Permissions.CANCEL_LEAVE_REQUEST]);
  }

  get canCancel(): boolean {
    if (this.isMe) {
      // A request the caller reviews can be decided but not cancelled.
      if (this.request.mine === false) return false;
      // Own pending leave, or approved leave that has not ended yet.
      return !this.loadingDetail && (this.isPending || (this.request.status === 'APPROVED' && !this.hasEnded));
    }
    // Review mode: approvers can't decide their own request, but may still cancel it.
    return this.mayCancelStaffRequest && this.isPending && (this.mode === 'view' || this.isSelfRequest);
  }

  private get hasEnded(): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(this.request.endDate || '');
    if (!m) return false;
    const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end < today;
  }

  formatDate(date: string | null): string {
    return formatTenantDate(date);
  }

  getLeaveTypeColor(): string {
    const name = this.request.policy?.name || '';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return PALETTE[hash % PALETTE.length];
  }

  close() {
    this.dialogRef.close();
  }

  requestCancel() {
    this.dialogRef.close({ cancel: true });
  }

  decide(decision: 'approve' | 'reject') {
    if (this.submitting || !this.canDecide) return;
    this.submitting = decision;
    this.errorMessage = null;
    const note = (this.reviewForm.get('reviewComment')?.value || '').trim() || undefined;
    const decide$ = this.isMe
      ? (decision === 'approve' ? this.myApi.approveLeaveRequest(this.request.id, note) : this.myApi.rejectLeaveRequest(this.request.id, note))
      : this.api.decideLeaveRequest(this.request.id, decision, note);
    decide$
      .pipe(finalize(() => (this.submitting = null)))
      .subscribe({
        next: (updated) => {
          this.notification.success(
            decision === 'approve'
              ? $localize`:@@leaveRequestApproved:Leave request approved successfully`
              : $localize`:@@leaveRequestRejected:Leave request rejected successfully`,
          );
          this.dialogRef.close({ updated });
        },
        error: (err: StaffModuleError) => {
          this.errorMessage = err.message;
          this.notification.error(err.message);
        },
      });
  }
}
